import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type {
  AdminMarketingDomainStatsResponse,
  MarketingDomainDailyPoint,
  MarketingDomainHostStats,
} from '@/lib/types';

const RANGE_OPTIONS = [
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];
const ALLOWED_DAYS = new Set<string>(RANGE_OPTIONS.map((r) => r.days));

type HostSortKey =
  | 'host'
  | 'destination'
  | 'visits'
  | 'human_visits'
  | 'unique_ips'
  | 'other_bot_visits'
  | 'claude_bot_visits'
  | 'non_landing_visits'
  | 'attributed_signups'
  | 'conversion_rate'
  | 'last_visit'
  | 'serve_visits'
  | 'redirect_visits';

function otherBotVisits(row: { bot_visits: number; claude_bot_visits: number }): number {
  return Math.max(0, row.bot_visits - row.claude_bot_visits);
}

function recordedVisits(row: {
  human_visits: number;
  bot_visits: number;
  non_landing_visits: number;
}): number {
  return row.human_visits + row.bot_visits + row.non_landing_visits;
}

function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>{value}</p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
      <p className='mt-0.5 text-sm font-semibold tabular-nums text-gray-900'>{value}</p>
    </div>
  );
}

const HOST_SORT_OPTIONS: Array<[HostSortKey, string]> = [
  ['visits', 'Visits'],
  ['human_visits', 'Human'],
  ['unique_ips', 'Uniques'],
  ['other_bot_visits', 'Other bots'],
  ['claude_bot_visits', 'Claude'],
  ['non_landing_visits', 'Not human'],
  ['attributed_signups', 'Signups'],
  ['conversion_rate', 'Conversion'],
  ['last_visit', 'Last visit'],
  ['serve_visits', 'Serve'],
  ['redirect_visits', 'Redirect'],
  ['host', 'Domain'],
  ['destination', 'Destination'],
];

function DomainHostCard({ row }: { row: MarketingDomainHostStats }) {
  return (
    <article className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='break-all font-mono text-sm font-semibold text-gray-900'>{row.host}</p>
      <p className='mt-0.5 break-all font-mono text-xs text-gray-500'>{row.destination}</p>
      <div className='mt-3 grid grid-cols-2 gap-x-4 gap-y-3'>
        <Metric label='Visits' value={formatNumber(row.visits)} />
        <Metric label='Human' value={formatNumber(row.human_visits)} />
        <Metric label='Uniques' value={formatNumber(row.unique_ips)} />
        <Metric label='Signups' value={formatNumber(row.attributed_signups)} />
        <Metric label='Other bots' value={formatNumber(otherBotVisits(row))} />
        <Metric label='Claude' value={formatNumber(row.claude_bot_visits)} />
        <Metric label='Not human' value={formatNumber(row.non_landing_visits)} />
        <Metric label='Serve' value={formatNumber(row.serve_visits)} />
        <Metric label='Redirect' value={formatNumber(row.redirect_visits)} />
        <Metric label='Conv.' value={formatPercent(row.conversion_rate)} />
        <div className='col-span-2'>
          <Metric label='Last visit' value={formatTimestamp(row.last_visit)} />
        </div>
      </div>
    </article>
  );
}

function TrendChart({ data }: { data: MarketingDomainDailyPoint[] }) {
  if (!data.length) {
    return <p className='text-sm text-gray-500'>No visits in this range.</p>;
  }
  const max = Math.max(
    1,
    ...data.map((d) =>
      Math.max(
        d.human_visits,
        otherBotVisits(d),
        d.claude_bot_visits,
        d.non_landing_visits,
        d.attributed_signups,
      ),
    ),
  );
  return (
    <div className='space-y-1'>
      <div className='mb-2 flex flex-wrap items-center gap-4 text-xs text-gray-500'>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-sky-500' /> Human visits
        </span>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-gray-300' /> Other bots
        </span>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-amber-400' /> Claude
        </span>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-rose-300' /> Not human
        </span>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-emerald-500' /> Signups
        </span>
      </div>
      <div className='flex h-36 items-end gap-[2px]'>
        {data.map((d) => {
          const otherBots = otherBotVisits(d);
          const humanH = (d.human_visits / max) * 100;
          const botH = (otherBots / max) * 100;
          const claudeH = (d.claude_bot_visits / max) * 100;
          const nonLandingH = (d.non_landing_visits / max) * 100;
          const signupH = (d.attributed_signups / max) * 100;
          return (
            <div
              key={d.date}
              className='group relative flex min-w-[3px] flex-1 items-end gap-[1px]'
              style={{ height: '100%' }}
              title={`${d.date}: ${d.human_visits} human, ${d.unique_visitors} uniques, ${otherBots} other bots, ${d.claude_bot_visits} Claude, ${d.non_landing_visits} not human, ${d.attributed_signups} signups`}
            >
              <div className='w-full rounded-t-sm bg-sky-500' style={{ height: `${humanH}%` }} />
              <div className='w-full rounded-t-sm bg-gray-300' style={{ height: `${botH}%` }} />
              <div className='w-full rounded-t-sm bg-amber-400' style={{ height: `${claudeH}%` }} />
              <div className='w-full rounded-t-sm bg-rose-300' style={{ height: `${nonLandingH}%` }} />
              <div
                className='w-full rounded-t-sm bg-emerald-500'
                style={{ height: `${signupH}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className='flex justify-between pt-1 text-[10px] text-gray-400'>
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function compareHosts(
  a: MarketingDomainHostStats,
  b: MarketingDomainHostStats,
  key: HostSortKey,
): number {
  if (key === 'host' || key === 'destination') {
    return a[key].localeCompare(b[key]);
  }
  if (key === 'last_visit') {
    return (a.last_visit ?? '').localeCompare(b.last_visit ?? '');
  }
  if (key === 'other_bot_visits') {
    return otherBotVisits(a) - otherBotVisits(b);
  }
  return a[key] - b[key];
}

function exportHostsCsv(hosts: MarketingDomainHostStats[], days: number) {
  const header = [
    'host',
    'destination',
    'visits',
    'human_visits',
    'unique_visitors',
    'other_bot_visits',
    'claude_bot_visits',
    'non_landing_visits',
    'serve_visits',
    'redirect_visits',
    'attributed_signups',
    'conversion_rate',
    'last_visit',
  ];
  const rows = hosts.map((row) => [
    row.host,
    row.destination,
    String(row.visits),
    String(row.human_visits),
    String(row.unique_ips),
    String(otherBotVisits(row)),
    String(row.claude_bot_visits),
    String(row.non_landing_visits),
    String(row.serve_visits),
    String(row.redirect_visits),
    String(row.attributed_signups),
    row.conversion_rate.toFixed(4),
    row.last_visit ?? '',
  ]);
  const csv = [header, ...rows]
    .map((line) => line.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `marketing-domains-${days}d.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function AdminMarketingDomainsPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate({ from: '/admin/marketing-domains' });
  const { days, host } = Route.useSearch();
  const daysNum = Number(days);
  const [sortKey, setSortKey] = useState<HostSortKey>('visits');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  const statsQuery = useQuery({
    queryKey: ['admin', 'marketing-domains', daysNum, host ?? 'all'],
    queryFn: () =>
      apiJson<AdminMarketingDomainStatsResponse>(
        getToken,
        `/v1/admin/marketing-domains/stats?days=${daysNum}${
          host ? `&host=${encodeURIComponent(host)}` : ''
        }`,
      ),
  });

  const data = statsQuery.data;
  const sortedHosts = useMemo(() => {
    const hosts = [...(data?.hosts ?? [])];
    hosts.sort((a, b) => {
      const cmp = compareHosts(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return hosts;
  }, [data?.hosts, sortDir, sortKey]);

  const toggleSort = (key: HostSortKey) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'host' || key === 'destination' ? 'asc' : 'desc');
  };

  const sortHint = (key: HostSortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>Marketing Domains</h1>
          <p className='mt-1 text-sm text-gray-600'>
            Pass-through visits on purchased keyword domains, with attributed signups matched by
            utm_source. Unique visitors are distinct IPs only. Human is computed when this page
            loads — it is not a stored column — so we can tighten the definition without rewriting
            history.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <label className='text-sm text-gray-600'>
            Range
            <select
              className='ml-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm'
              value={days}
              onChange={(event) =>
                void navigate({
                  search: { days: event.target.value as RangeDays, host },
                })
              }
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.days} value={option.days}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className='text-sm text-gray-600'>
            Domain
            <select
              className='ml-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm'
              value={host ?? ''}
              onChange={(event) =>
                void navigate({
                  search: {
                    days,
                    host: event.target.value || undefined,
                  },
                })
              }
            >
              <option value=''>All domains</option>
              {(data?.known_hosts ?? []).map((apex) => (
                <option key={apex} value={apex}>
                  {apex}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {statsQuery.isLoading ? (
        <div className='flex justify-center py-16'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : null}

      {statsQuery.isError ? (
        <div className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          Failed to load marketing-domain stats.
        </div>
      ) : null}

      {data ? (
        <>
          <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4'>
            <StatCard
              label='Human visits'
              value={formatNumber(data.overview.human_visits)}
              hint={`${daysNum}d · GET + landing path + browser navigation headers`}
            />
            <StatCard
              label='Unique visitors'
              value={formatNumber(data.overview.unique_visitors)}
              hint='Distinct IPs, humans only'
            />
            <StatCard
              label='Not human'
              value={formatNumber(data.overview.non_landing_visits)}
              hint={`${formatPercent(
                data.overview.non_landing_visits / Math.max(1, recordedVisits(data.overview)),
              )} of visits · probes, scanners, missing browser signals`}
            />
            <StatCard
              label='Other bots'
              value={formatNumber(otherBotVisits(data.overview))}
              hint={`${formatPercent(
                otherBotVisits(data.overview) / Math.max(1, recordedVisits(data.overview)),
              )} of visits`}
            />
            <StatCard
              label='Claude'
              value={formatNumber(data.overview.claude_bot_visits)}
              hint={`${formatPercent(
                data.overview.claude_bot_visits / Math.max(1, recordedVisits(data.overview)),
              )} of visits · ClaudeBot / Claude-Web`}
            />
            <StatCard
              label='Attributed signups'
              value={formatNumber(data.overview.attributed_signups)}
              hint='utm_source = domain'
            />
            <StatCard
              label='Visit → signup'
              value={formatPercent(data.overview.visit_to_signup_rate)}
              hint='Signups / human visits'
            />
            <StatCard
              label='Domains'
              value={formatNumber(data.hosts.length)}
              hint={data.host ? data.host : 'Purchased apex hosts'}
            />
            <StatCard
              label='Serve visits'
              value={formatNumber(data.overview.serve_visits)}
              hint='Self-canonical satellite pages'
            />
            <StatCard
              label='Redirect visits'
              value={formatNumber(data.overview.redirect_visits)}
              hint='302 to harborcoparent.com'
            />
          </div>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <h2 className='text-lg font-semibold text-gray-900'>Trends</h2>
            <TrendChart data={data.daily} />
          </section>

          <section className='space-y-3 md:rounded-lg md:border md:border-gray-200 md:bg-white md:p-4 md:shadow-sm'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <h2 className='text-lg font-semibold text-gray-900'>Domains</h2>
              <button
                type='button'
                onClick={() => exportHostsCsv(sortedHosts, daysNum)}
                className='inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50'
              >
                <FiDownload className='h-4 w-4' />
                Export CSV
              </button>
            </div>
            <div className='flex items-center gap-2 md:hidden'>
              <label className='text-sm text-gray-600'>
                Sort
                <select
                  className='ml-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm'
                  value={sortKey}
                  onChange={(event) => {
                    const next = event.target.value as HostSortKey;
                    setSortKey(next);
                    setSortDir(next === 'host' || next === 'destination' ? 'asc' : 'desc');
                  }}
                >
                  {HOST_SORT_OPTIONS.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type='button'
                onClick={() =>
                  setSortDir((current) => (current === 'desc' ? 'asc' : 'desc'))
                }
                className='rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700'
                aria-label={sortDir === 'desc' ? 'Sort descending' : 'Sort ascending'}
              >
                {sortDir === 'desc' ? '↓' : '↑'}
              </button>
            </div>
            {sortedHosts.length === 0 ? (
              <p className='py-6 text-center text-sm text-gray-400 md:hidden'>
                No marketing-domain visits in this range.
              </p>
            ) : (
              <div className='space-y-3 md:hidden'>
                {sortedHosts.map((row) => (
                  <DomainHostCard key={row.host} row={row} />
                ))}
              </div>
            )}
            <div className='hidden overflow-x-auto rounded-md border border-gray-200 md:block'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                  <tr>
                    {(
                      [
                        ['host', 'Domain'],
                        ['destination', 'Destination'],
                        ['visits', 'Visits'],
                        ['human_visits', 'Human'],
                        ['unique_ips', 'Uniques'],
                        ['other_bot_visits', 'Other bots'],
                        ['claude_bot_visits', 'Claude'],
                        ['non_landing_visits', 'Not human'],
                        ['serve_visits', 'Serve'],
                        ['redirect_visits', 'Redirect'],
                        ['attributed_signups', 'Signups'],
                        ['conversion_rate', 'Conv.'],
                        ['last_visit', 'Last visit'],
                      ] as Array<[HostSortKey, string]>
                    ).map(([key, label]) => (
                      <th key={key} className='px-4 py-3 text-left'>
                        <button
                          type='button'
                          onClick={() => toggleSort(key)}
                          className='font-semibold uppercase tracking-wide hover:text-gray-800'
                        >
                          {label}
                          {sortHint(key)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {sortedHosts.length === 0 ? (
                    <tr>
                      <td colSpan={13} className='px-4 py-8 text-center text-gray-400'>
                        No marketing-domain visits in this range.
                      </td>
                    </tr>
                  ) : (
                    sortedHosts.map((row) => (
                      <tr key={row.host} className='hover:bg-gray-50'>
                        <td className='px-4 py-2 font-mono text-gray-800'>{row.host}</td>
                        <td className='px-4 py-2 font-mono text-gray-600'>{row.destination}</td>
                        <td className='px-4 py-2 tabular-nums'>{formatNumber(row.visits)}</td>
                        <td className='px-4 py-2 tabular-nums'>{formatNumber(row.human_visits)}</td>
                        <td className='px-4 py-2 tabular-nums'>{formatNumber(row.unique_ips)}</td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(otherBotVisits(row))}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.claude_bot_visits)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.non_landing_visits)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.serve_visits)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.redirect_visits)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.attributed_signups)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatPercent(row.conversion_rate)}
                        </td>
                        <td className='px-4 py-2 text-gray-600'>
                          {formatTimestamp(row.last_visit)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <section className='space-y-3 md:rounded-lg md:border md:border-gray-200 md:bg-white md:p-4 md:shadow-sm'>
              <h2 className='text-lg font-semibold text-gray-900'>Referrers</h2>
              {data.referrers.length === 0 ? (
                <p className='py-6 text-center text-sm text-gray-400 md:hidden'>
                  No referrers in this range.
                </p>
              ) : (
                <div className='space-y-3 md:hidden'>
                  {data.referrers.map((row) => (
                    <article
                      key={row.referrer}
                      className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
                    >
                      <p className='break-all text-sm font-medium text-gray-900'>{row.referrer}</p>
                      <div className='mt-3 grid grid-cols-2 gap-x-4 gap-y-3'>
                        <Metric label='Visits' value={formatNumber(row.visits)} />
                        <Metric label='Human' value={formatNumber(row.human_visits)} />
                      </div>
                    </article>
                  ))}
                </div>
              )}
              <div className='hidden overflow-x-auto rounded-md border border-gray-200 md:block'>
                <table className='w-full text-sm'>
                  <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                    <tr>
                      <th className='px-4 py-3 text-left'>Referrer</th>
                      <th className='px-4 py-3 text-right'>Visits</th>
                      <th className='px-4 py-3 text-right'>Human</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {data.referrers.length === 0 ? (
                      <tr>
                        <td colSpan={3} className='px-4 py-8 text-center text-gray-400'>
                          No referrers in this range.
                        </td>
                      </tr>
                    ) : (
                      data.referrers.map((row) => (
                        <tr key={row.referrer} className='hover:bg-gray-50'>
                          <td className='px-4 py-2 text-gray-800'>{row.referrer}</td>
                          <td className='px-4 py-2 text-right tabular-nums'>
                            {formatNumber(row.visits)}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums'>
                            {formatNumber(row.human_visits)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className='space-y-3 md:rounded-lg md:border md:border-gray-200 md:bg-white md:p-4 md:shadow-sm'>
              <h2 className='text-lg font-semibold text-gray-900'>Destinations</h2>
              {data.destinations.length === 0 ? (
                <p className='py-6 text-center text-sm text-gray-400 md:hidden'>
                  No destinations in this range.
                </p>
              ) : (
                <div className='space-y-3 md:hidden'>
                  {data.destinations.map((row) => (
                    <article
                      key={row.destination}
                      className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
                    >
                      <p className='break-all font-mono text-sm font-semibold text-gray-900'>
                        {row.destination}
                      </p>
                      <div className='mt-3 grid grid-cols-2 gap-x-4 gap-y-3'>
                        <Metric label='Human' value={formatNumber(row.human_visits)} />
                        <Metric label='Uniques' value={formatNumber(row.unique_visitors)} />
                        <Metric label='Signups' value={formatNumber(row.attributed_signups)} />
                        <Metric label='Conv.' value={formatPercent(row.conversion_rate)} />
                      </div>
                    </article>
                  ))}
                </div>
              )}
              <div className='hidden overflow-x-auto rounded-md border border-gray-200 md:block'>
                <table className='w-full text-sm'>
                  <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                    <tr>
                      <th className='px-4 py-3 text-left'>Path</th>
                      <th className='px-4 py-3 text-right'>Human</th>
                      <th className='px-4 py-3 text-right'>Uniques</th>
                      <th className='px-4 py-3 text-right'>Signups</th>
                      <th className='px-4 py-3 text-right'>Conv.</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {data.destinations.length === 0 ? (
                      <tr>
                        <td colSpan={5} className='px-4 py-8 text-center text-gray-400'>
                          No destinations in this range.
                        </td>
                      </tr>
                    ) : (
                      data.destinations.map((row) => (
                        <tr key={row.destination} className='hover:bg-gray-50'>
                          <td className='px-4 py-2 font-mono text-gray-800'>{row.destination}</td>
                          <td className='px-4 py-2 text-right tabular-nums'>
                            {formatNumber(row.human_visits)}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums'>
                            {formatNumber(row.unique_visitors)}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums'>
                            {formatNumber(row.attributed_signups)}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums'>
                            {formatPercent(row.conversion_rate)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/marketing-domains')({
  component: AdminMarketingDomainsPage,
  validateSearch: (search: Record<string, unknown>) => {
    const daysRaw = typeof search.days === 'string' ? search.days : '30';
    const hostRaw = typeof search.host === 'string' ? search.host : undefined;
    return {
      days: (ALLOWED_DAYS.has(daysRaw) ? daysRaw : '30') as RangeDays,
      host: hostRaw || undefined,
    };
  },
});
